import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    Box,
    Stack,
    Typography,
    TextField,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    IconButton,
    InputAdornment,
    Divider,
    useMediaQuery,
    useTheme,
    Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { LoadingButton } from '@mui/lab';
import { SystemUserTypes } from '../../types';
import { useGenericQuery } from '../../services/react-query/generic.query';
import { useGenericMutation } from '../../services/react-query/generic.mutation';
import { handleApiError } from '../../services/react-query/react-query.client';
import { toast } from 'react-toastify';

interface Props {
    visible: boolean;
    onClose: () => void;
    currentOwner?: SystemUserTypes.Model;
    onSuccess?: () => void;
}



export const ChangeOrganizationOwnerModal: React.FC<Props> = ({
    visible,
    onClose,
    currentOwner,
    onSuccess,
}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUser, setSelectedUser] = useState<SystemUserTypes.Model | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch super admins from API
    const {
        data: superAdmins = [],
        isLoading: isLoadingSuperAdmins,
        refetch: refetchSuperAdmins,
    } = useGenericQuery<SystemUserTypes.Model[]>(
        'getSuperAdminsByCustomer',
        'customerWebAppUser/getSuperAdminsByCustomer',
        undefined,
        undefined,
        visible // Only fetch when modal is visible
    );

    // Mutation for changing subscription owner
    const { mutateAsync: changeSubscriptionOwner, isLoading: isChangingOwner } = useGenericMutation<
        { newOwnerId: string },
        any
    >(
        'changeSubscriptionOwner',
        'customer/changeSubscriptionOwner',
        'post'
    );

    // Filter super admins based on search term
    const filteredSuperAdmins = useMemo(() => {
        return superAdmins.filter((admin: SystemUserTypes.Model) =>
            admin.fullname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            admin.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [superAdmins, searchTerm]);

    const handleUserSelect = (user: SystemUserTypes.Model) => {
        setSelectedUser(user);
    };

    const handleConfirm = async () => {
        if (!selectedUser) return;
        
        try {
            await changeSubscriptionOwner(
                { newOwnerId: selectedUser.id },
                {
                    onError: (error) => {
                        const errorMessage = handleApiError(error);
                        toast(errorMessage, {
                            autoClose: 3000,
                            type: 'error',
                        });
                    },
                    onSuccess: () => {
                        toast('Organization owner contact information updated successfully!', {
                            autoClose: 3000,
                            type: 'success',
                        });
                        onClose();
                        // Refetch super admins to update the list
                        refetchSuperAdmins();
                        // Call the onSuccess callback to refresh parent data
                        onSuccess?.();
                    },
                }
            );
        } catch (error) {
            console.error('Error changing organization owner:', error);
        }
    };

    const getInitials = (fullname: string) => {
        const names = fullname.split(' ');
        return names.length >= 2 
            ? `${names[0].charAt(0)}${names[1].charAt(0)}`
            : fullname.charAt(0);
    };

    return (
        <Dialog
            open={visible}
            onClose={onClose}
            maxWidth="md"
            fullWidth={isSmallScreen}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    minHeight: 400,
                }
            }}
        >
            {/* Header */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 3 }}>
                    <Typography fontSize={20} fontWeight={600} color="text.primary">
                        Change Organization Owner
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            height: 32,
                            width: 32,
                            color: 'text.secondary',
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </Box>

            <DialogContent sx={{ p: 3 }}>
                {/* Current Owner Info */}
                {currentOwner && (
                    <Box sx={{ mb: 3, p: 2, bgcolor: 'background.neutral', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Current Organization Owner
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar 
                                sx={{ bgcolor: 'primary.main', color: '#ffffff' }}
                            >
                                {getInitials(currentOwner.fullname)}
                            </Avatar>
                            <Box>
                                <Typography fontWeight={600}>
                                    {currentOwner.fullname}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {currentOwner.email}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                )}

                {/* Search Bar */}
                <TextField
                    fullWidth
                    placeholder="Search admins by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 3 }}
                />

                {/* Super Admins List */}
                <Box>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        Select a new organization owner from the admins below:
                    </Typography>
                    
                    <List sx={{ maxHeight: 400, overflow: 'auto', bgcolor: 'background.paper', borderRadius: 1 }}>
                        {isLoadingSuperAdmins ? (
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography color="text.secondary" textAlign="center">
                                            Loading admins...
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ) : filteredSuperAdmins.length > 0 ? (
                            filteredSuperAdmins.map((admin, index) => (
                                <React.Fragment key={admin.id}>
                                    <ListItem
                                        onClick={() => handleUserSelect(admin)}
                                        sx={{
                                            my: 1,
                                            borderRadius: 1,
                                            cursor: 'pointer',
                                            bgcolor: selectedUser?.id === admin.id ? 'primary.light' : 'transparent',
                                            '&:hover': {
                                                bgcolor: selectedUser?.id === admin.id ? 'primary.light' : 'action.hover',
                                            },
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar 
                                                sx={{ 
                                                    bgcolor: selectedUser?.id === admin.id ? 'primary.main' : 'background.neutral',
                                                    color: selectedUser?.id === admin.id ? '#ffffff' : 'text.secondary'
                                                }}
                                            >
                                                {getInitials(admin.fullname)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Typography fontWeight={600}>
                                                    {admin.fullname}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {admin.email}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {admin.phone}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                    {index < filteredSuperAdmins.length - 1 && <Divider />}
                                </React.Fragment>
                            ))
                        ) : (
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography color="text.secondary" textAlign="center">
                                            No admins found matching your search
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )}
                    </List>
                </Box>
            </DialogContent>

            {/* Footer Actions */}
            <Box sx={{ borderTop: 1, borderColor: 'divider', p: 3 }}>
                <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 0.5 }}
                        >
                            {selectedUser ? `Selected: ${selectedUser.fullname}` : 'Please select an admin'}
                        </Typography>
                        
                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            {filteredSuperAdmins.length} admin{filteredSuperAdmins.length !== 1 ? 's' : ''} found
                        </Typography>
                    </Box>
                    
                    <Stack direction="row" spacing={2}>
                        <Button
                            onClick={onClose}
                            disabled={isChangingOwner}
                            variant="outlined"
                            color="inherit"
                        >
                            Cancel
                        </Button>
                        <LoadingButton
                            onClick={handleConfirm}
                            loading={isChangingOwner}
                            disabled={!selectedUser || isChangingOwner}
                            variant="contained"
                            color="primary"
                        >
                            Save Changes
                        </LoadingButton>
                    </Stack>
                </Stack>
            </Box>
        </Dialog>
    );
}; 